import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FaClipboardList,
  FaLeaf,
  FaShoppingBag,
  FaUsers,
  FaSyncAlt,
  FaExclamationCircle,
} from "react-icons/fa";
import { AuthContext } from "../context/AuthContext";
import WelcomeScreen from "../components/WelcomeScreen";
import { authHeaders } from "../utils/authHeaders";
import "./Dashboard.css";

const API =
  (process.env.REACT_APP_API_URL && process.env.REACT_APP_API_URL.replace(/\/$/, "")) ||
  `${window.location.protocol}//${window.location.hostname}:3000/api`;

const ORDER_STATUS = {
  pending: "Pending",
  accepted: "Diterima",
  rejected: "Ditolak",
  completed: "Selesai",
};

function normStatus(value) {
  const status = String(value || "").toLowerCase();
  return ORDER_STATUS[status] ? status : "pending";
}

function normalizeText(value) {
  return String(value || "").trim();
}

function formatWholeNumber(value) {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return normalizeText(value);
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

function getSurveyKabupaten(submission) {
  const answer = (submission?.answers || []).find((item) =>
    String(item?.question_text || "").toLowerCase().includes("kabupaten")
  );
  return normalizeText(answer?.answer_text || answer?.answer_number || "");
}

function isSurveyDetailQuestionHidden(questionText) {
  const text = String(questionText || "").toLowerCase();
  const isCloneProductionQuestion = text.includes("produksi bibit");

  return (
    text.includes("nama sahabat") ||
    text.includes("nama responden") ||
    text.includes("nama petani") ||
    text.includes("kabupaten lokasi") ||
    text === "kabupaten" ||
    text.includes("lokasi sahabat kakao") ||
    text.includes("jenis clone") ||
    isCloneProductionQuestion
  );
}

function parseSurveyAnswerValues(answer) {
  if (answer?.answer_number != null && String(answer.answer_number).trim() !== "") {
    return [formatWholeNumber(answer.answer_number)];
  }

  const text = normalizeText(answer?.answer_text);
  if (text) {
    if (/^https?:\/\//i.test(text) || /\.(png|jpe?g|gif|webp|pdf|docx?|xlsx?)$/i.test(text)) {
      return ["Lampiran / Media"];
    }
    if (/^-?\d+([.,]\d+)?$/.test(text.replace(/,/g, ""))) {
      return [formatWholeNumber(text.replace(/,/g, ""))];
    }
    return [text];
  }

  if (answer?.answer_json != null && String(answer.answer_json).trim() !== "") {
    try {
      const parsed = typeof answer.answer_json === "string" ? JSON.parse(answer.answer_json) : answer.answer_json;
      if (Array.isArray(parsed)) return parsed.map((item) => normalizeText(item)).filter(Boolean);
      if (parsed && typeof parsed === "object") {
        return Object.entries(parsed)
          .map(([key, value]) => {
            if (value === true) return key;
            if (value === false || value == null || value === "") return "";
            return `${key}: ${value}`;
          })
          .filter(Boolean);
      }
      return [normalizeText(parsed)].filter(Boolean);
    } catch {
      return [String(answer.answer_json)];
    }
  }

  return [];
}

function getProductionClone(questionText) {
  const text = String(questionText || "").toLowerCase();
  if (!text.includes("produksi bibit")) return "";
  if (/\bmcc\s*0?2\b|\bmcc02\b|mcc02\/45/.test(text)) return "MCC02";
  if (/\bbb\s*0?1\b|\bbb01\b/.test(text)) return "BB01";
  if (/\bs\s*1\b|\bs1\b|pbc123/.test(text)) return "S1";
  if (/\bs\s*2\b|\bs2\b|br25/.test(text)) return "S2";
  if (/\bmcc\s*0?1\b|\bmcc01\b|\bm01\b/.test(text)) return "MCC01";
  if (/\bm0?4\b/.test(text)) return "M04";
  if (/\bm0?6\b/.test(text)) return "M06";
  if (/\bisc\s*1\b|\bisc1\b/.test(text)) return "ISC1";
  if (/\bscc\s*11\b|\bscc11\b/.test(text)) return "SCC11";
  return "";
}

function parseNumericAnswer(answer) {
  if (answer?.answer_number != null && String(answer.answer_number).trim() !== "") {
    const n = Number(answer.answer_number);
    return Number.isFinite(n) ? n : 0;
  }

  const text = normalizeText(answer?.answer_text).replace(/,/g, "");
  if (!text) return 0;
  const n = Number(text);
  return Number.isFinite(n) ? n : 0;
}

async function readJson(res) {
  const json = await res.json();
  if (!res.ok || !json?.success) {
    throw new Error(json?.message || `HTTP ${res.status}`);
  }
  return json;
}

function StatCard({ icon, label, value, meta }) {
  return (
    <div className="dash-stat">
      <div className="dash-stat-icon">{icon}</div>
      <div>
        <div className="dash-stat-label">{label}</div>
        <div className="dash-stat-value">{value}</div>
        {meta && <div className="dash-stat-meta">{meta}</div>}
      </div>
    </div>
  );
}

function BarRow({ label, value, max, tone = "green" }) {
  const numericValue = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  const pct = max > 0 && Number.isFinite(numericValue) ? Math.max(4, Math.round((numericValue / max) * 100)) : 0;
  return (
    <div className="dash-bar-row">
      <div className="dash-bar-label">
        <span>{label}</span>
        <strong>{value}</strong>
      </div>
      <div className="dash-bar-track">
        <div className={`dash-bar-fill ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [showWelcome, setShowWelcome] = useState(() => !sessionStorage.getItem("dashboard_seen"));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [surveyTitles, setSurveyTitles] = useState([]);
  const [surveySubmissions, setSurveySubmissions] = useState([]);
  const [aoRows, setAoRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [scopeSummary, setScopeSummary] = useState({ members: 0, services: 0, orders: 0, orders_by_status: {} });
  const [dataFilter, setDataFilter] = useState("all");
  const [kabupatenFilter, setKabupatenFilter] = useState("");

  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isFieldTeam = role === "field team";

  async function loadDashboard() {
    if (!user?.role) return;
    setLoading(true);
    setError("");

    try {
      if (isAdmin) {
        const headers = authHeaders(user);
        const [titlesJson, aoJson, ordersJson] = await Promise.all([
          fetch(`${API}/raw-data/surveys/titles`, { headers, credentials: "include" }).then(readJson),
          fetch(`${API}/raw-data/ao?limit=5000`, { headers, credentials: "include" }).then(readJson),
          fetch(`${API}/raw-data/orders`, { headers, credentials: "include" }).then(readJson),
        ]);

        const titles = titlesJson.data || [];
        const surveyResults = await Promise.all(
          titles.map((survey) =>
            fetch(`${API}/raw-data/surveys?survey_id=${encodeURIComponent(survey.survey_id)}`, {
              headers,
              credentials: "include",
            })
              .then(readJson)
              .then((json) => ({
                ...survey,
                submissions: json.submissions || [],
              }))
              .catch(() => ({ ...survey, submissions: [] }))
          )
        );

        setSurveyTitles(surveyResults);
        setSurveySubmissions(surveyResults.flatMap((survey) => survey.submissions || []));
        setAoRows(aoJson.data || []);
        setOrders(ordersJson.data || []);
      } else if (isFieldTeam) {
        const headers = authHeaders(user);
        const [ordersJson, summaryJson] = await Promise.all([
          fetch(`${API}/business/orders?scope=institution&limit=500`, {
            headers,
            credentials: "include",
          }).then(readJson),
          fetch(`${API}/business/scope-summary`, {
            headers,
            credentials: "include",
          }).then(readJson),
        ]);
        setOrders(ordersJson.data || []);
        setScopeSummary(summaryJson.data || { members: 0, services: 0, orders: 0, orders_by_status: {} });
        setAoRows([]);
        setSurveyTitles([]);
        setSurveySubmissions([]);
      } else {
        const headers = authHeaders(user);
        const ordersJson = await fetch(
          `${API}/business/orders?user_id=${encodeURIComponent(user?.id || "")}&limit=200`,
          { headers, credentials: "include" }
        ).then(readJson);
        setOrders(ordersJson.data || []);

        try {
          const localAo = JSON.parse(localStorage.getItem("ao_evaluation_history") || "[]");
          setAoRows(Array.isArray(localAo) ? localAo : []);
        } catch {
          setAoRows([]);
        }
        setSurveyTitles([]);
        setSurveySubmissions([]);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Gagal memuat dashboard.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, user?.id, user?.institution]);

  const kabupatenOptions = useMemo(() => {
    const values = new Set();
    aoRows.forEach((row) => {
      const value = normalizeText(row.kabupaten || row.district);
      if (value) values.add(value);
    });
    orders.forEach((order) => {
      const value = normalizeText(order.buyer_kabupaten);
      if (value) values.add(value);
    });
    surveySubmissions.forEach((submission) => {
      const value = getSurveyKabupaten(submission);
      if (value) values.add(value);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, "id"));
  }, [aoRows, orders, surveySubmissions]);

  const filteredData = useMemo(() => {
    const allowSurvey = dataFilter === "all" || dataFilter === "survey";
    const allowAo = dataFilter === "all" || dataFilter === "ao";
    const allowOrder = dataFilter === "all" || dataFilter === "order";
    const targetKabupaten = normalizeText(kabupatenFilter).toLowerCase();
    const matchesKabupaten = (value) => {
      if (!targetKabupaten) return true;
      return normalizeText(value).toLowerCase() === targetKabupaten;
    };

    const surveys = allowSurvey
      ? surveySubmissions.filter((submission) => matchesKabupaten(getSurveyKabupaten(submission)))
      : [];
    const surveyIds = new Set(surveys.map((submission) => Number(submission.survey_id)));
    const surveyGroups = allowSurvey
      ? surveyTitles
          .map((survey) => ({
            ...survey,
            submissions: (survey.submissions || []).filter((submission) =>
              matchesKabupaten(getSurveyKabupaten(submission))
            ),
          }))
          .filter((survey) => !targetKabupaten || survey.submissions.length > 0 || surveyIds.has(Number(survey.survey_id)))
      : [];

    return {
      surveyTitles: surveyGroups,
      surveySubmissions: surveys,
      aoRows: allowAo ? aoRows.filter((row) => matchesKabupaten(row.kabupaten || row.district)) : [],
      orders: allowOrder ? orders.filter((order) => matchesKabupaten(order.buyer_kabupaten)) : [],
    };
  }, [aoRows, dataFilter, kabupatenFilter, orders, surveySubmissions, surveyTitles]);

  const summary = useMemo(() => {
    const ordersByStatus = filteredData.orders.reduce((acc, order) => {
      const status = normStatus(order.status);
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const categoryCounts = filteredData.orders.reduce((acc, order) => {
      const key = order.category || "Lainnya";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const recommendationCounts = filteredData.aoRows.reduce((acc, row) => {
      const recommendations = Array.isArray(row.recommendations_json)
        ? row.recommendations_json
        : Array.isArray(row.recommendations)
          ? row.recommendations
          : [];
      recommendations.forEach((rec) => {
        const title = rec?.title || rec;
        if (title) acc[title] = (acc[title] || 0) + 1;
      });
      return acc;
    }, {});

    return {
      ordersByStatus,
      categoryCounts,
      recommendationCounts,
    };
  }, [filteredData]);

  const topSurveyTitles = [...filteredData.surveyTitles]
    .sort((a, b) => (b.submissions?.length || 0) - (a.submissions?.length || 0))
    .slice(0, 5);
  const topRecommendations = Object.entries(summary.recommendationCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const topCategories = Object.entries(summary.categoryCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxSurvey = Math.max(1, ...topSurveyTitles.map((survey) => survey.submissions?.length || 0));
  const maxRecommendation = Math.max(1, ...topRecommendations.map(([, value]) => value));
  const maxCategory = Math.max(1, ...topCategories.map(([, value]) => value));
  const surveyQuestionDetails = useMemo(() => {
    const map = new Map();

    filteredData.surveySubmissions.forEach((submission) => {
      (submission.answers || []).forEach((answer) => {
        const questionText = normalizeText(answer.question_text);
        if (!questionText) return;
        if (isSurveyDetailQuestionHidden(questionText)) return;

        const order = Number(answer.q_order);
        const key = `${Number.isFinite(order) ? order : "x"}-${questionText}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            order: Number.isFinite(order) ? order : 9999,
            question: questionText,
            answered: 0,
            counts: new Map(),
          });
        }

        const record = map.get(key);
        const values = parseSurveyAnswerValues(answer);
        if (!values.length) return;
        record.answered += 1;
        values.forEach((value) => {
          const label = normalizeText(value) || "Kosong";
          record.counts.set(label, (record.counts.get(label) || 0) + 1);
        });
      });
    });

    return Array.from(map.values())
      .map((record) => ({
        ...record,
        options: Array.from(record.counts.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6),
      }))
      .filter((record) => record.options.length > 0)
      .sort((a, b) => a.order - b.order || a.question.localeCompare(b.question, "id"))
      .slice(0, 12);
  }, [filteredData.surveySubmissions]);
  const surveyServiceChart = useMemo(() => {
    const counts = new Map();

    filteredData.surveySubmissions.forEach((submission) => {
      const services = new Set();
      (submission.answers || []).forEach((answer) => {
        const question = String(answer.question_text || "").toLowerCase();
        const isServiceQuestion =
          question.includes("jasa") &&
          (question.includes("servis") || question.includes("service") || question.includes("layanan"));
        if (!isServiceQuestion) return;

        parseSurveyAnswerValues(answer).forEach((value) => {
          const label = normalizeText(value);
          if (label) services.add(label);
        });
      });

      services.forEach((service) => {
        counts.set(service, (counts.get(service) || 0) + 1);
      });
    });

    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [filteredData.surveySubmissions]);
  const surveyCloneProductionChart = useMemo(() => {
    const totals = new Map();

    filteredData.surveySubmissions.forEach((submission) => {
      (submission.answers || []).forEach((answer) => {
        const clone = getProductionClone(answer.question_text);
        if (!clone) return;

        const value = parseNumericAnswer(answer);
        if (value <= 0) return;
        totals.set(clone, (totals.get(clone) || 0) + value);
      });
    });

    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
  }, [filteredData.surveySubmissions]);
  const maxSurveyService = Math.max(1, ...surveyServiceChart.map(([, value]) => value));
  const maxSurveyCloneProduction = Math.max(1, ...surveyCloneProductionChart.map(([, value]) => value));
  const totalFiltered = filteredData.surveySubmissions.length + filteredData.aoRows.length + filteredData.orders.length;
  const showSurveyCharts = dataFilter === "all" || dataFilter === "survey";
  const showAoCharts = dataFilter === "all" || dataFilter === "ao";
  const showOrderCharts = dataFilter === "all" || dataFilter === "order";

  return (
    <div className="dashboard-page">
      <div className="dash-head">
        <div>
          <h1>Dashboard</h1>
          <p>
            {isFieldTeam
              ? `Performa Sahabat Kakao dalam institusi ${user?.institution || "akun Anda"}.`
              : "Ringkasan Hasil Survey, Data AO, dan Order dari data yang tersedia."}
          </p>
        </div>
        <div className="dash-head-controls">
          <label>
            <span>Data</span>
            <select value={dataFilter} onChange={(e) => setDataFilter(e.target.value)}>
              <option value="all">Semua Data</option>
              <option value="survey">Survey</option>
              <option value="ao">AO</option>
              <option value="order">Order</option>
            </select>
          </label>
          <label>
            <span>Kabupaten</span>
            <select value={kabupatenFilter} onChange={(e) => setKabupatenFilter(e.target.value)}>
              <option value="">Semua Kabupaten</option>
              {kabupatenOptions.map((kabupaten) => (
                <option key={kabupaten} value={kabupaten}>
                  {kabupaten}
                </option>
              ))}
            </select>
          </label>
          <button
            className="dash-filter-reset"
            type="button"
            onClick={() => {
              setDataFilter("all");
              setKabupatenFilter("");
            }}
          >
            Reset Filter
          </button>
          <div className="dash-filter-count">{totalFiltered} data tampil</div>
          <button className="dash-refresh" onClick={loadDashboard} disabled={loading}>
            <FaSyncAlt /> {loading ? "Memuat..." : "Refresh"}
          </button>
        </div>
      </div>

      {!isAdmin && (
        <div className="dash-note">
          <FaExclamationCircle />
          <span>
            {isFieldTeam
              ? "Data dibatasi otomatis ke Sahabat Kakao yang memiliki institusi sama. Pengelolaan data tetap dilakukan oleh pemilik akun."
              : "Data survey dan raw AO lengkap hanya tersedia untuk admin. Tampilan ini memakai data order dan AO lokal akun Anda."}
          </span>
        </div>
      )}

      {error && <div className="dash-error">{error}</div>}

      <div className="dash-stats">
        {isFieldTeam ? (
          <>
            <StatCard icon={<FaUsers />} label="Sahabat Kakao" value={scopeSummary.members} meta="Dalam institusi yang sama" />
            <StatCard icon={<FaLeaf />} label="Layanan Usaha" value={scopeSummary.services} meta="Layanan terdaftar" />
          </>
        ) : (
          <>
            <StatCard icon={<FaClipboardList />} label="Hasil Survey" value={filteredData.surveySubmissions.length} meta={`${filteredData.surveyTitles.length} survey aktif`} />
            <StatCard icon={<FaLeaf />} label="Data AO" value={filteredData.aoRows.length} meta={`${topRecommendations.length} rekomendasi teratas`} />
          </>
        )}
        <StatCard icon={<FaShoppingBag />} label="Data Order" value={filteredData.orders.length} meta={`${summary.ordersByStatus.pending || 0} pending`} />
      </div>

      <div className="dash-grid">
        {showSurveyCharts && (
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Hasil Survey</h2>
              <span>{filteredData.surveySubmissions.length} respons</span>
            </div>
            {topSurveyTitles.length ? (
              topSurveyTitles.map((survey) => (
                <BarRow key={survey.survey_id} label={survey.title} value={survey.submissions?.length || 0} max={maxSurvey} />
              ))
            ) : (
              <div className="dash-empty">Belum ada hasil survey.</div>
            )}
          </section>
        )}

        {showSurveyCharts && (
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Petani Berdasarkan Jasa Servis</h2>
              <span>{filteredData.surveySubmissions.length} respons</span>
            </div>
            {surveyServiceChart.length ? (
              surveyServiceChart.map(([label, value]) => (
                <BarRow key={label} label={label} value={value} max={maxSurveyService} tone="blue" />
              ))
            ) : (
              <div className="dash-empty">Belum ada data jasa servis.</div>
            )}
          </section>
        )}

        {showSurveyCharts && (
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Produksi Bibit Berdasarkan Clone</h2>
              <span>Total produksi</span>
            </div>
            {surveyCloneProductionChart.length ? (
              surveyCloneProductionChart.map(([label, value]) => (
                <BarRow
                  key={label}
                  label={label}
                  value={formatWholeNumber(value)}
                  max={maxSurveyCloneProduction}
                  tone="green"
                />
              ))
            ) : (
              <div className="dash-empty">Belum ada data produksi bibit.</div>
            )}
          </section>
        )}

        {showSurveyCharts && (
          <section className="dash-panel dash-survey-detail-panel">
            <div className="dash-panel-head">
              <h2>Detail Isi Form Survey</h2>
              <span>{surveyQuestionDetails.length} pertanyaan</span>
            </div>
            {surveyQuestionDetails.length ? (
              <div className="dash-question-grid">
                {surveyQuestionDetails.map((question) => {
                  const maxAnswer = Math.max(1, ...question.options.map(([, value]) => value));
                  return (
                    <div className="dash-question-card" key={question.key}>
                      <div className="dash-question-title">
                        <strong>{question.question}</strong>
                        <span>{question.answered} terjawab</span>
                      </div>
                      {question.options.map(([label, value]) => (
                        <BarRow key={label} label={label} value={value} max={maxAnswer} tone="green" />
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="dash-empty">Belum ada jawaban survey untuk ditampilkan.</div>
            )}
          </section>
        )}

        {showOrderCharts && (
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Status Order</h2>
              <span>{filteredData.orders.length} order</span>
            </div>
            {Object.entries(ORDER_STATUS).map(([key, label]) => (
              <BarRow
                key={key}
                label={label}
                value={summary.ordersByStatus[key] || 0}
                max={Math.max(1, filteredData.orders.length)}
                tone={key}
              />
            ))}
          </section>
        )}

        {showAoCharts && (
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Rekomendasi AO</h2>
              <span>Teratas</span>
            </div>
            {topRecommendations.length ? (
              topRecommendations.map(([label, value]) => (
                <BarRow key={label} label={label} value={value} max={maxRecommendation} tone="amber" />
              ))
            ) : (
              <div className="dash-empty">Belum ada rekomendasi.</div>
            )}
          </section>
        )}

        {showOrderCharts && (
          <section className="dash-panel">
            <div className="dash-panel-head">
              <h2>Kategori Order</h2>
              <span>Terbanyak</span>
            </div>
            {topCategories.length ? (
              topCategories.map(([label, value]) => <BarRow key={label} label={label} value={value} max={maxCategory} tone="blue" />)
            ) : (
              <div className="dash-empty">Belum ada order.</div>
            )}
          </section>
        )}

      </div>

      {showWelcome && (
        <WelcomeScreen
          onClose={() => {
            sessionStorage.setItem("dashboard_seen", "1");
            setShowWelcome(false);
          }}
        />
      )}
    </div>
  );
}
